import pullFlatModules from '../../pages/flat-modules';
import categoryFilterExclude from '../../services/category-filter-exclude';
import mName from '../../services/module-name';
import { ajax } from '../../utils/ajaxHandler';
import { get } from '../../utils/objectHelpers';
import LoadingSpinner from '../str-loading-spinner/component';

const CATEGORY_FILTER_EVENT = 'categoryFilterEvent';
const SORT_LISTINGS_EVENT = 'sortListingsEvent';
const POPSTATE_UPDATE_EVENT = 'popstateUpdateEvent';
const AJAX_TIMEOUT = 10000;

// Modules to update by event
const CAT_EVT_MODULE_NAMES = [
    mName.ITEMS_GRID,
    mName.SORT_ITEMS_SELECTION,
    mName.SEARCH_STORE_V2
];
const SORT_EVT_MODULE_NAMES = [
    mName.ITEMS_GRID,
    mName.STORE_CATEGORIES_V2
];
const MIN_EVT_MODULE_NAMES = [
    mName.ITEMS_GRID,
    mName.STORE_CATEGORIES_V2,
    mName.SORT_ITEMS_SELECTION,
    mName.SEARCH_STORE_V2
];
const MAX_EVT_MODULE_NAMES = [
    ...categoryFilterExclude.moduleNames,
    ...MIN_EVT_MODULE_NAMES
];

export default class {
    onCreate(input, out) {
        this.defaultLanguage = get(out, 'global.req.locality.defaultLanguage');
        this.state = {
            regionsData: input
        };
    }
    onMount() {
        document.addEventListener('ajax-module-update-event', evt => {
            this.updateModules({
                url: get(evt, 'detail.url'),
                updateEvent: get(evt, 'detail.updateEvent'),
                errorCallback: get(evt, 'detail.errorCallback')
            });
        });
        window.addEventListener('popstate', (evt) => {
            const updateEvent = evt.state && evt.state.updateEvent;

            if (updateEvent) {
                const spinnerId = getSpinnerId(updateEvent);
                LoadingSpinner.show(spinnerId);
                this.updateModules({
                    url: window.location.href,
                    updateEvent: POPSTATE_UPDATE_EVENT,
                    spinnerId: spinnerId
                });
            }
        });
    }
    // The param cfg: {
    //     url: string,
    //     updateEvent: string
    //     spinnerId: string
    //     errorCallback: function
    // }
    updateModules(cfg) {
        // Show spinner
        let spinnerId = cfg.spinnerId;
        if (!spinnerId) {
            spinnerId = getSpinnerId(cfg.updateEvent);
        }
        LoadingSpinner.show(spinnerId);

        const ajaxUrl = buildAjaxUrl(cfg.url, cfg.updateEvent);

        ajax({ url: ajaxUrl }, {
            successCallBack: (resp) => {
                if (this.isError(resp)) {
                    if (cfg.errorCallback) {
                        cfg.errorCallback();
                    }
                } else {
                    const newRegionsData = pullFlatModules(resp, this.defaultLanguage);
                    if (cfg.updateEvent === CATEGORY_FILTER_EVENT) {
                        updateForCategoryFilter(this.state, newRegionsData, cfg.url);
                    } else if (cfg.updateEvent === SORT_LISTINGS_EVENT) {
                        updateForSortFilter(this.state, newRegionsData, cfg.url);
                    } else if (cfg.updateEvent === POPSTATE_UPDATE_EVENT) {
                        fullUpdate(this.state, newRegionsData);
                    }
                }
                LoadingSpinner.hide(spinnerId);
            },
            errorCallBack: () => {
                if (cfg.errorCallback) {
                    cfg.errorCallback();
                }
                LoadingSpinner.hide(spinnerId);
            }
        },
        AJAX_TIMEOUT);
    }
    isError(resp) {
        return !!(resp.message || resp.stack); // Assumes resp.message and resp.stack are errors
    }
}

function buildAjaxUrl(url, updateEvent) {
    const link = document.createElement('a');
    link.href = url;
    const supportedUxComponents = buildSupportedUxComponents(url, updateEvent);
    let queryString;

    if (link.search) {
        queryString = `${link.search}&supportedUxComponents=${supportedUxComponents}`;
    } else {
        queryString = `?supportedUxComponents=${supportedUxComponents}`;
    }

    return `https://${window.location.host}${link.pathname}${queryString}&_ajax=itemFilter`;
}

// Determine which modules to request.
// The modules requested and returned determine the correctness of the ajax update.
// The Category and Sort menus need updating when their option urls change.
// The Search module needs updating when a Category is selected, as its placeholder text changes.
function buildSupportedUxComponents(url, updateEvent) {
    const matrix = {
        noParams: { catEvt: MAX_EVT_MODULE_NAMES, sortEvt: SORT_EVT_MODULE_NAMES, popEvt: MAX_EVT_MODULE_NAMES },
        catParam: { catEvt: CAT_EVT_MODULE_NAMES, sortEvt: SORT_EVT_MODULE_NAMES, popEvt: MIN_EVT_MODULE_NAMES },
        sortParam: { catEvt: MAX_EVT_MODULE_NAMES, sortEvt: SORT_EVT_MODULE_NAMES, popEvt: MAX_EVT_MODULE_NAMES }
    };

    let paramKey;
    // The order of the checks matter here, since the
    // category filter removes/replaces modules.
    if (hasCategoryParam(url)) {
        paramKey = 'catParam';
    } else if (hasSortParam(url)) {
        paramKey = 'sortParam';
    } else {
        paramKey = 'noParams';
    }

    let evtKey;
    if (updateEvent === CATEGORY_FILTER_EVENT) {
        evtKey = 'catEvt';
    } else if (updateEvent === SORT_LISTINGS_EVENT) {
        evtKey = 'sortEvt';
    } else {
        evtKey = 'popEvt';
    }

    const moduleList = matrix[paramKey][evtKey];
    return moduleList;
}

function hasSortParam(url) {
    return url.match(/_sop=\d+/);
}

function hasCategoryParam(url) {
    return url.match(/_sacat=\d+|store_cat=\d+|_storecat=\d+/);
}

// Updates for Category filter and removes modules listed in categoryFilterExclude.moduleNames.
// But if "All Categories" filter (no category selected), restores the removed modules instead.
function updateForCategoryFilter(state, newRegionsData, url) {
    if (hasCategoryParam(url) && newRegionsData && newRegionsData.TAB0 && newRegionsData.MAIN) {
        const regionsDataClone = JSON.parse(JSON.stringify(state.regionsData));

        // Remove modules
        for (let i = regionsDataClone.TAB0.length - 1; i >= 0; i--) {
            const module = regionsDataClone.TAB0[i];
            const name = get(module, 'meta.name', '');
            if (categoryFilterExclude.moduleNames.includes(name)) {
                regionsDataClone.TAB0.splice(i, 1);
            }
        }
        // Replace modules
        let replaceCount = replaceModules(regionsDataClone.TAB0, newRegionsData.TAB0, CAT_EVT_MODULE_NAMES);
        replaceCount += replaceModules(regionsDataClone.MAIN, newRegionsData.MAIN, CAT_EVT_MODULE_NAMES);

        // Add one because currently, the Search module is in both TAB0 and MAIN.
        // If the service removes Search from TAB0, we can stop adding one.
        if (replaceCount === CAT_EVT_MODULE_NAMES.length + 1) {
            state.regionsData = { ...regionsDataClone }; // Marko should repaint on state update
            updateWindowHistory(url, CATEGORY_FILTER_EVENT);
        }
    } else {
        // Restore the removed modules
        fullUpdate(state, newRegionsData);
        updateWindowHistory(url, CATEGORY_FILTER_EVENT);
    }
}

function updateForSortFilter(state, newRegionsData, url) {
    if (newRegionsData && newRegionsData.TAB0) {
        const regionsDataClone = JSON.parse(JSON.stringify(state.regionsData));
        const replaceCount = replaceModules(regionsDataClone.TAB0, newRegionsData.TAB0, SORT_EVT_MODULE_NAMES);
        if (replaceCount === SORT_EVT_MODULE_NAMES.length) {
            state.regionsData = { ...regionsDataClone };
            updateWindowHistory(url, SORT_LISTINGS_EVENT);
        }
    }
}

// The full update only works if the missing modules are requested
// from the service and the service returns all of them.
// Updates the TAB0 modules and the Search module.
function fullUpdate(state, newRegionsData) {
    if (newRegionsData && newRegionsData.MAIN && newRegionsData.TAB0) {
        const regionsDataClone = JSON.parse(JSON.stringify(state.regionsData));
        regionsDataClone.TAB0 = newRegionsData.TAB0;
        const replaceCount = replaceModules(regionsDataClone.MAIN, newRegionsData.MAIN, [mName.SEARCH_STORE_V2]);
        if (replaceCount) {
            state.regionsData = { ...regionsDataClone };
        }
    }
}

// Return the number of modules replaced.
function replaceModules(modules, newModules, moduleNames) {
    let replaceCount = 0;
    modules.forEach((module, idx) => {
        const name = get(module, 'meta.name', '');
        if (moduleNames.includes(name)) {
            const mod = newModules.find(m => m.meta && m.meta.name === name);
            if (mod) {
                modules[idx] = mod;
                replaceCount++;
            }
        }
    });
    return replaceCount;
}

function updateWindowHistory(url, updateEvent) {
    try {
        const link = document.createElement('a');
        link.href = url;
        const newUrl = `https://${window.location.host}${link.pathname}${link.search}${window.location.hash}`;
        window.history.replaceState({ updateEvent: updateEvent }, '');
        window.history.pushState({ updateEvent: updateEvent }, '', newUrl);
    } catch (err) {
        // console.log(err);
    }
}

function getSpinnerId(updateEvent) {
    let spinnerId = 'tab0LoadingSpinner';
    if (updateEvent === SORT_LISTINGS_EVENT) {
        spinnerId = 'listingsLoadingSpinner';
    }
    return spinnerId;
}
